// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductAddToCartButton from "@mcmc/Overrides/Components/ProductAddToCartButton";
import ProductDescription, { ProductDescriptionStyles } from "@mcmc/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@mcmc/Overrides/Components/ProductPartNumbers";
import { price } from "@mcmc/Overrides/Widgets/ProductList/ProductCard.styles";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import { Roboto } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productInfoWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productDescriptionWrapper?: InjectableCss;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonWrapper?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addToListWrapper?: InjectableCss;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    cardTopSectionWrapper?: InjectableCss;
}

const productDescriptionStyle: TypographyProps = {
    ...Roboto,
    size: 18,
    lineHeight: "21px",
    weight: "normal",
    css: css`
        letter-spacing: 0;
    `,
};

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0;
            margin: 0;
            transition: box-shadow 0.2s ease-in-out;
            width: 100%;
            position: relative;

            &:hover {
                box-shadow: ${getProp("theme.shadows.2")};
            }
        `,
    },
    productImageWrapper: {
        css: css`
            height: 187px;
            width: 100%;
            border-bottom: 1px solid ${getColor("common.ui3")};
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 100%;
                height: 100%;
                justify-content: center;

                &:focus {
                    outline: none;
                }
            `,
        },
        image: {
            css: css``,
        },
    },
    productInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            padding: 25px 30px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: productDescriptionStyle,
        },
        productDetailDescription: productDescriptionStyle,
    },
    productDescriptionWrapper: {
        css: css`
            height: 67px;
            margin-bottom: 12px;
        `,
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                margin-bottom: 13px;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            margin: 25px 0 15px;
        `,
    },
    productPriceStyles: price,
    addToCartWrapper: {
        css: css`
            display: flex;
        `,
    },
    qtyWrapper: {
        css: css`
            flex: 1 0 64px;
            max-width: 100px;
            margin-right: 8px;
        `,
    },
    productQuantityOrderedStyles: {
        labelProps: {
            size: 13,
            weight: 400,
            css: css`
                width: 30px;
                padding-right: 0;
                text-align: left;
                letter-spacing: 0.17px;
            `,
        },
        labelPosition: "left",
        cssOverrides: {
            formInputWrapper: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")}
                flex: 1;
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                padding-right: 15px !important;
            `,
            formField: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                margin-right: 8px;
                flex: 1 0 72px;
            `,
        },
    },
    addToCartButtonWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
        `,
    },
    productAddToCartButtonStyles: {
        sizeVariant: "medium",
        css: css`
            min-width: 130px;
        `,
    },
    addToListWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-top: 15px;
            ${StyledWrapper} {
                width: auto;
                height: auto;
                margin-left: auto;
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: {
                size: 16,
                css: css`
                    letter-spacing: 0.19px;
                `,
            },
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    cardTopSectionWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    settingsCollection,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    // XNG-Changes: MCMC-2197 move useOverlayBadge, useWidgetBadges above return null, to fix issu
    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        // XNG-Changes
        productContext: { productInfo: createFromProduct(product), product },
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({
        // XNG-Changes
        productContext: { productInfo: createFromProduct(product), product },
        showTextBadges,
        showImageBadges,
    });

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { productSettings } = settingsCollection;
    const showUOM = !!(
        product.unitOfMeasures &&
        product.unitOfMeasures.filter(uom => uom.unitOfMeasure).length > 1 &&
        productSettings.alternateUnitsOfMeasure
    );

    return (
        <StyledBox
            {...styles.productWrapper}
            onFocus={event => {
                event.stopPropagation();
            }}
        >
            <ProductContext.Provider value={productContext}>
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {showBrand && product.brand && (
                    <StyledWrapper {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.productInfoWrapper}>
                    <StyledWrapper {...styles.cardTopSectionWrapper}>
                        {showTitle && (
                            <StyledWrapper {...styles.productDescriptionWrapper}>
                                <ProductDescription
                                    product={productContext}
                                    includeBrandName={true}
                                    maxLine={3}
                                    onClick={() => trackProductRecommendation(true)}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            </StyledWrapper>
                        )}
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                customerProductReference={product.properties?.referenceNumbers}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                        )}
                    </StyledWrapper>
                    <ProductContextAvailability />
                    {showPrice && (
                        <StyledWrapper {...styles.productPriceWrapper}>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.productPriceStyles}
                            />
                            <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                        </StyledWrapper>
                    )}
                    {showAddToCart && product.canAddToCart && (
                        <StyledWrapper {...styles.addToCartWrapper}>
                            <StyledWrapper {...styles.qtyWrapper}>
                                <ProductQuantityOrdered
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${productContext.product.id}-qty`}
                                />
                            </StyledWrapper>
                            {showUOM && (
                                <>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        id={`${carouselId}-uom`}
                                    />
                                </>
                            )}
                            <StyledWrapper {...styles.addToCartButtonWrapper}>
                                <ProductAddToCartButton
                                    onAddToCartClick={() => trackProductRecommendation(false)}
                                    iconOnly={showUOM}
                                    data-test-selector="addToCartBtn"
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {(showAddToList || actionsTemplate) && (
                        <StyledWrapper {...styles.addToListWrapper}>
                            {showAddToList && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                />
                            )}
                            {actionsTemplate ? actionsTemplate(product) : []}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </ProductContext.Provider>
        </StyledBox>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
